import { apiClient } from '@/lib/api-client';
import type {
  CloseTotvsOperationalAlertInput,
  CreateTotvsProductLinkInput,
  ListTotvsOperationalAlertsInput,
  OperationalAlertFacet,
  TotvsOperationalAlertStatus,
  ListTotvsProductLinksInput,
  ListTotvsSyncTasksInput,
  Paginated,
  ProcessTotvsSyncTaskNowInput,
  ProcessTotvsSyncTaskNowOutput,
  ReprocessBatchTotvsSyncsInput,
  ReprocessBatchTotvsSyncsOutput,
  ReprocessTotvsSyncTaskInput,
  TotvsEntityEventsResponse,
  TotvsOperationalAlert,
  TotvsProductLink,
  TotvsProductLinkMatchInput,
  TotvsSyncTask,
  UpdateTotvsProductLinkInput,
  UpdateTotvsSyncTaskPayloadInput,
} from '../../domain/entities/totvs.entity';

export class TotvsApiDatasource {
  async listSyncTasks(
    input?: ListTotvsSyncTasksInput,
  ): Promise<Paginated<TotvsSyncTask>> {
    const { data } = await apiClient.get<Paginated<TotvsSyncTask>>(
      '/admin/totvs/sync-tasks',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          status: input?.status,
          productExternalId: input?.productExternalId || undefined,
          errorCode: input?.errorCode || undefined,
          userId: input?.userId || undefined,
        },
      },
    );
    return data;
  }

  async getSyncTask(id: string): Promise<TotvsSyncTask> {
    const { data } = await apiClient.get<TotvsSyncTask>(
      `/admin/totvs/sync-tasks/${id}`,
    );
    return data;
  }

  async reprocessSyncTask(
    id: string,
    input?: ReprocessTotvsSyncTaskInput,
  ): Promise<TotvsSyncTask> {
    const { data } = await apiClient.post<TotvsSyncTask>(
      `/admin/totvs/sync-tasks/${id}/reprocess`,
      input ?? {},
    );
    return data;
  }

  async processSyncTaskNow(
    id: string,
    input?: ProcessTotvsSyncTaskNowInput,
  ): Promise<ProcessTotvsSyncTaskNowOutput> {
    const { data } = await apiClient.post<ProcessTotvsSyncTaskNowOutput>(
      `/admin/totvs/sync-tasks/${id}/process-now`,
      input ?? {},
    );
    return data;
  }

  async updateSyncTaskPayload(
    id: string,
    input: UpdateTotvsSyncTaskPayloadInput,
  ): Promise<TotvsSyncTask> {
    const { data } = await apiClient.patch<TotvsSyncTask>(
      `/admin/totvs/sync-tasks/${id}/payload`,
      input,
    );
    return data;
  }

  async reprocessBatch(
    input: ReprocessBatchTotvsSyncsInput,
  ): Promise<ReprocessBatchTotvsSyncsOutput> {
    const { data } = await apiClient.post<ReprocessBatchTotvsSyncsOutput>(
      '/admin/totvs/sync-tasks/reprocess-batch',
      input,
    );
    return data;
  }

  async listProductLinks(
    input?: ListTotvsProductLinksInput,
  ): Promise<Paginated<TotvsProductLink>> {
    const { data } = await apiClient.get<Paginated<TotvsProductLink>>(
      '/admin/totvs/product-links',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          provider: input?.provider || undefined,
          providerAccountId: input?.providerAccountId || undefined,
          productExternalId: input?.productExternalId || undefined,
          offerExternalId: input?.offerExternalId || undefined,
          action: input?.action || undefined,
          idAreaInteresse: input?.idAreaInteresse || undefined,
          codColigada: input?.codColigada,
          codFilial: input?.codFilial,
          idPs: input?.idPs || undefined,
          enabled: input?.enabled,
        },
      },
    );
    return data;
  }

  /**
   * O de-para que **vale** para uma chave — aplica a precedência
   * específico-vence-curinga. Diferente de `listProductLinks`, que é
   * igualdade simples e perderia o curinga que realmente vai valer. Sem de-para,
   * o Nest responde `null` com corpo vazio (`''` no axios); o `|| null` devolve
   * o `null` que o tipo promete.
   */
  async getProductLinkMatch(
    input: TotvsProductLinkMatchInput,
  ): Promise<TotvsProductLink | null> {
    const { data } = await apiClient.get<TotvsProductLink | ''>(
      '/admin/totvs/product-links/match',
      { params: input },
    );
    return data || null;
  }

  async createProductLink(
    input: CreateTotvsProductLinkInput,
  ): Promise<TotvsProductLink> {
    const { data } = await apiClient.post<TotvsProductLink>(
      '/admin/totvs/product-links',
      input,
    );
    return data;
  }

  async updateProductLink(
    id: string,
    input: UpdateTotvsProductLinkInput,
  ): Promise<TotvsProductLink> {
    const { data } = await apiClient.patch<TotvsProductLink>(
      `/admin/totvs/product-links/${id}`,
      input,
    );
    return data;
  }

  async deleteProductLink(id: string): Promise<TotvsProductLink> {
    const { data } = await apiClient.delete<TotvsProductLink>(
      `/admin/totvs/product-links/${id}`,
    );
    return data;
  }

  // --- Alertas operacionais (Fase 5 do plano de alertas compartilhados) ---

  async listOperationalAlertFacets(
    status?: TotvsOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    const { data } = await apiClient.get<OperationalAlertFacet[]>(
      '/admin/totvs/operational-alerts/facets',
      { params: { status } },
    );
    return data;
  }

  async listOperationalAlerts(
    input?: ListTotvsOperationalAlertsInput,
  ): Promise<Paginated<TotvsOperationalAlert>> {
    const { data } = await apiClient.get<Paginated<TotvsOperationalAlert>>(
      '/admin/totvs/operational-alerts',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          status: input?.status || undefined,
          type: input?.type || undefined,
          entityType: input?.entityType || undefined,
          entityId: input?.entityId || undefined,
          order: input?.order,
        },
      },
    );
    return data;
  }

  async resolveOperationalAlert(
    id: string,
    input: CloseTotvsOperationalAlertInput,
  ): Promise<TotvsOperationalAlert> {
    const { data } = await apiClient.post<TotvsOperationalAlert>(
      `/admin/totvs/operational-alerts/${id}/resolve`,
      input,
    );
    return data;
  }

  async dismissOperationalAlert(
    id: string,
    input: CloseTotvsOperationalAlertInput,
  ): Promise<TotvsOperationalAlert> {
    const { data } = await apiClient.post<TotvsOperationalAlert>(
      `/admin/totvs/operational-alerts/${id}/dismiss`,
      input,
    );
    return data;
  }

  async getEntityEvents(
    entityType: string,
    entityId: string,
    params: { page: number; perPage: number },
  ): Promise<TotvsEntityEventsResponse> {
    const { data } = await apiClient.get<TotvsEntityEventsResponse>(
      `/admin/totvs/entities/${entityType}/${entityId}/events`,
      { params },
    );
    return data;
  }
}
